import { formatTimestamp } from "../utils/timestamps.mjs";
import React, { useState } from "react";
import { Plus, FileText, ShieldCheck } from "lucide-react";
import { formatDate } from "../utils/calendar.mjs";
import { Panel, Empty } from "./ui";
export function Priority({ client }) {
  return (
    <details className="priorityReasons" open>
      <summary>
        Pourquoi cette note ? <b>{client.score}/100</b>
      </summary>
      <ul>
        {client.scoreFactors?.map((f, i) => (
          <li key={i}>
            <b>
              {f.points > 0 ? "+" : ""}
              {f.points}
            </b>{" "}
            {f.label}
          </li>
        ))}
      </ul>
      {client.scoreCapped && (
        <small>
          {client.scoreRaw < 0 ? "Score ramené à 0." : "Score plafonné à 100."}
        </small>
      )}
    </details>
  );
}
export function Signals({ signals }) {
  return signals.length ? (
    signals.map((s) => (
      <div className="signalCard" key={s.id}>
        <span className="status">
          {s.status === "nouveau" ? "🔥 Nouveau signal" : s.status}
        </span>
        <h3>
          {s.job || s.type || "Signal commercial"}
          {s.qty ? ` × ${s.qty}` : ""}
        </h3>
        <p>{s.content}</p>
        <small>
          Source : {s.source}
          <br />
          Publié / détecté le {formatTimestamp(s.date)} · Importé le{" "}
          {formatTimestamp(s.importedAt)}
        </small>
      </div>
    ))
  ) : (
    <Empty>Aucun signal commercial connu.</Empty>
  );
}
export function Timeline({ state, client }) {
  const events = state.events
    .filter((e) => e.clientId === client.id)
    .slice()
    .reverse();
  return (
    <div className="timeline">
      {events.map((e) => (
        <div className="history" key={e.id}>
          <div className="timelineDot" />
          <div>
            <b>
              {formatTimestamp(e.createdAt)}
            </b>
            <p>{e.label}</p>
          </div>
        </div>
      ))}
      <details>
        <summary>Historique conservé ({client.history.length})</summary>
        {[...client.history].reverse().map((h, i) => (
          <div className="history" key={i}>
            {h}
          </div>
        ))}
      </details>
    </div>
  );
}
export default function ClientRecord({
  state,
  client,
  onContact,
  onEdit,
  onQuote,
  onNeed,
  onOwner,
  compact = false,
}) {
  const [section, setSection] = useState("summary");
  const signals = state.signals
    .filter((s) => s.clientId === client.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const tasks = state.tasks
    .filter((t) => t.clientId === client.id && !t.done)
    .sort((a, b) => a.due_date_original.localeCompare(b.due_date_original));
  const needs = state.needs.filter((n) => n.clientId === client.id),
    quotes = state.quotes.filter((q) => q.clientId === client.id);
  const lossAt = client.lossReasonAt || [...state.results].reverse().find(r => r.clientId === client.id && r.label === "Client perdu" && r.details?.lossReason === client.lossReason)?.createdAt;
  const sections = [
    ["summary", "Synthèse"],
    ["contacts", `Contacts (${client.contacts.length})`],
    ["signals", `Signaux (${signals.length})`],
    ["history", "Historique"],
    ["needs", `Besoins (${needs.length})`],
    ["quotes", `Devis (${quotes.length})`],
    ["activity", "Activité / Commandes"],
  ];
  return (
    <Panel
      title={client.name}
      action={
        onOwner && (
          <button className="textAction" onClick={onOwner}>
            <ShieldCheck />
            {client.owner || "Commercial non renseigné"}
          </button>
        )
      }
    >
      <div className="companySummary">
        <div>
          <span className="status">{client.status}</span>
          {client.riskReason && (
            <span className="status riskBadge">🔴 {client.riskReason}</span>
          )}
          <p className="muted">
            {client.city} · {client.sector}
            <br />
            SIRET {client.siret} · SIREN {client.siren || "À compléter"}
            <br />
            Agence : {client.agency} · Commercial : {client.owner}
            {client.secondaryOwner
              ? ` · Secondaire : ${client.secondaryOwner}`
              : ""}
          </p>
        </div>
        <div className="score">
          <strong>{client.score}</strong>
          <small>/100</small>
        </div>
      </div>
      <nav className="detailTabs" aria-label="Fiche établissement">
        {sections.map(([key, label]) => (
          <button
            key={key}
            className={section === key ? "active" : ""}
            aria-current={section === key ? "page" : undefined}
            onClick={() => setSection(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {section === "summary" && (
        <>
          <dl className="clientDetails">
            <dt>Raison sociale</dt>
            <dd>{client.name}</dd>
            <dt>Enseigne</dt>
            <dd>{client.tradeName || "Non renseignée"}</dd>
            <dt>Adresse</dt>
            <dd>
              {client.address || "À compléter"} {client.postalCode}{" "}
              {client.city}
            </dd>
            <dt>Téléphone / Email</dt>
            <dd>
              {client.phone || "À compléter"}
              <br />
              {client.email || "À compléter"}
            </dd>
            <dt>Prochaine action</dt>
            <dd>
              {tasks[0]
                ? `${tasks[0].title} · ${formatDate(tasks[0].due_date_original)}`
                : "Aucune action prévue"}
            </dd>
            <dt>Dernier résultat</dt>
            <dd>{client.lastCallResult || "Aucun résultat enregistré"}{client.lastCallResult && <small>{formatTimestamp(client.lastContactAt)}</small>}</dd>
            <dt>Dernier contact</dt>
            <dd>
              {client.lastContactAt
                ? new Date(client.lastContactAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                : "Non renseigné"}
            </dd>
            <dt>Dernière commande</dt>
            <dd>
              {client.lastOrder?.includes("/")
                ? client.lastOrder
                : formatDate(client.lastOrder)}
            </dd>
            <dt>Besoins ouverts</dt>
            <dd>
              {
                needs.filter(
                  (n) => !["signé", "perdu", "suspendu"].includes(n.status),
                ).length
              }
            </dd>
            <dt>Devis en cours</dt>
            <dd>
              {
                quotes.filter((q) => !["refusé", "signé"].includes(q.status))
                  .length
              }
            </dd>
          </dl>
          <Signals signals={signals.slice(0, compact ? 1 : 2)} />
          {client.lossReason && (
            <div className="notice riskBadge">
              Motif historique de perte : {client.lossReason}
              <small>Enregistré le {formatTimestamp(lossAt)}</small>
            </div>
          )}
        </>
      )}
      {section === "contacts" && (
        <>
          <button
            className="secondary"
            onClick={() => onContact({ replace: false })}
          >
            <Plus />
            Ajouter un contact
          </button>
          {client.contacts.map((c) => (
            <div
              className={`contact ${!c.active ? "inactive" : ""}`}
              key={c.id}
            >
              <div>
                <b>{c.name}</b>{" "}
                <span className="status">
                  {!c.active
                    ? `Inactif · ${c.inactiveReason}`
                    : c.primary
                      ? "Contact principal"
                      : "Actif"}
                </span>
              </div>
              <span>{c.role || "Fonction à compléter"}</span>
              <strong>{c.phone || "Téléphone à compléter"}</strong>
              <span>{c.email || "Email à compléter"}</span>
              <small>
                Meilleur moment : {c.bestTime || "Non renseigné"} ·
                Indisponibilité :{" "}
                {(c.unavailableDays || []).join(", ") || "Non renseignée"}
              </small>
              <small>Créé le {formatTimestamp(c.createdAt)}{c.updatedAt && ` · Modifié le ${formatTimestamp(c.updatedAt)}`}</small>
              <button className="textAction" onClick={() => onEdit(c)}>
                Modifier le contact
              </button>
            </div>
          ))}
        </>
      )}
      {section === "signals" && (
        <>
          <Signals signals={signals} />
          <Priority client={client} />
        </>
      )}
      {section === "history" && <Timeline state={state} client={client} />}
      {section === "needs" && (
        <>
          {onNeed && (
            <button className="secondary" onClick={onNeed}>
              <Plus />
              Créer un besoin commercial
            </button>
          )}
          {needs.map((n) => (
            <button
              key={n.id}
              className="opportunityRow"
              onClick={() => onQuote(n)}
            >
              <FileText />
              <div>
                <b>
                  {n.job} × {n.qty}
                </b>
                <small>
                  {n.type} · {n.status} · {formatDate(n.start)}
                  <br />Créé le {formatTimestamp(n.createdAt)}
                </small>
              </div>
            </button>
          ))}
          {!needs.length && <Empty>Aucun besoin.</Empty>}
        </>
      )}
      {section === "quotes" && (
        <>
          {quotes.map((q) => (
            <button
              key={q.id}
              className="opportunityRow"
              onClick={() => onQuote(needs.find((n) => n.id === q.needId))}
            >
              <FileText />
              <div>
                <b>Devis V{q.version}</b>
                <small>
                  {q.job} × {q.qty} · {q.status} · Conditions : {q.conditions}
                  <br />Créé le {formatTimestamp(q.createdAt)}
                  {q.sentAt && <><br />Envoyé le {formatTimestamp(q.sentAt)}</>}
                  {q.signedAt && <><br />Signé le {formatTimestamp(q.signedAt)}</>}
                  {q.refusedAt && <><br />Refusé le {formatTimestamp(q.refusedAt)}</>}
                </small>
              </div>
            </button>
          ))}
          {!quotes.length && <Empty>Aucun devis commercial enregistré.</Empty>}
          {client.quotes.map((q, i) => (
            <p className="muted" key={q.id || i}>
              Devis historique : {q.label || q.id} · {q.status}
            </p>
          ))}
        </>
      )}
      {section === "activity" && (
        <>
          <h3>Commandes / Missions ({client.orders.length})</h3>
          {client.orders.map((o) => (
            <div className="need" key={o.id}>
              <b>{formatDate(o.date)}</b>
              <span>
                {o.volume} postes ·{" "}
                {Number(o.amount || 0).toLocaleString("fr-FR")} € · Marge{" "}
                {Number(o.margin || 0).toLocaleString("fr-FR")} €
              </span>
            </div>
          ))}
          {!client.orders.length && <Empty>Aucune commande connue.</Empty>}
          <h3>Contrats conservés</h3>
          {client.contracts.map((c, i) => (
            <p className="muted" key={c.id || i}>
              {c.label || c.id} · {c.status}
            </p>
          ))}
          <h3>Factures conservées</h3>
          {client.invoices.map((f, i) => (
            <p className="muted" key={f.id || i}>
              {f.label || f.id} ·{" "}
              {Number(f.amount || 0).toLocaleString("fr-FR")} € · {f.status}
            </p>
          ))}
        </>
      )}
    </Panel>
  );
}
